import React, { useState, useEffect, useRef } from 'react';
import { CheckCircle2, Pause, Play, RotateCw, ExternalLink, PlugZap, Trash2, FileText, X, Pause as PauseIcon, LogIn, Eye, Square, Unplug, Plug } from 'lucide-react';
import api from '../../services/api';
import StatusPill from '../../components/StatusPill';
import CopyChip from '../../components/CopyChip';

export default function AdminUsers() {
  const [users, setUsers]     = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState({ role: '', status: '' });
  const [search, setSearch]   = useState('');
  const [attentionOnly, setAttentionOnly] = useState(false);
  const [page, setPage]       = useState(1);
  const [actionMsg, setActionMsg] = useState('');
  const [restarting, setRestarting] = useState({});
  const [testing, setTesting] = useState({});
  const [deleting, setDeleting] = useState({});
  const [verifying, setVerifying] = useState({});
  // accountId -> in-flight per-instance action label ('Stopping', 'Deleting'…)
  const [acting, setActing] = useState({});
  const [logsFor, setLogsFor] = useState(null);   // { id, email }

  useEffect(() => { loadUsers(); }, [filter, page]); // eslint-disable-line
  // Search / attention-filter changes are debounced and reset to page 1 —
  // the query runs server-side so results aren't limited to the visible page.
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); loadUsers(); }, 300);
    return () => clearTimeout(t);
  }, [search, attentionOnly]); // eslint-disable-line
  useEffect(() => {
    const id = setInterval(() => loadUsers({ silent: true }), 5000);
    return () => clearInterval(id);
  }, [filter, page, search, attentionOnly]); // eslint-disable-line

  async function loadUsers({ silent = false } = {}) {
    if (!silent) setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (filter.role)   params.role   = filter.role;
      if (filter.status) params.status = filter.status;
      if (search.trim()) params.search = search.trim();
      if (attentionOnly) params.containerStatus = 'needs_attention';
      const r = await api.get('/admin/users', { params });
      setUsers(r.data.users);
      setTotal(r.data.total);
    } catch (e) {
      // Silent 5s poll: swallow transient failures instead of spamming
      // unhandled rejections; the next tick retries anyway.
      if (!silent) setActionMsg('Error loading users: ' + (e.response?.data?.error || e.message));
    } finally {
      if (!silent) setLoading(false);
    }
  }

  // Container ops block server-side while the boot lock serializes MT5 boots
  // (30s–5min) — the global 30s axios timeout would abort a still-running op
  // and show a bogus "timeout" error while the server carries on.
  const LONG_OP = { timeout: 6 * 60_000 };

  // User-level verify: flips the person to verified AND boots every MT5
  // account of theirs that was waiting.
  async function verify(id) {
    setVerifying(v => ({ ...v, [id]: true }));
    setActionMsg('Verifying user — starting MT5 can take 1–5 minutes (boots are serialized). Leave this page open…');
    try {
      const r = await api.put(`/admin/users/${id}/verify`, null, LONG_OP);
      setActionMsg(r.data.message || 'User verified.');
      loadUsers();
    } catch (e) {
      const d = e.response?.data || {};
      const detail = d.detail ? ` — ${d.detail}` : '';
      const reason = d.reason ? ` [${d.reason}]` : '';
      setActionMsg(`Error: ${d.error || e.message}${reason}${detail}`);
      loadUsers();
    } finally {
      setVerifying(v => ({ ...v, [id]: false }));
    }
  }

  async function suspend(id) {
    if (!window.confirm('Suspend this user? Every MT5 container they own will be stopped.')) return;
    try {
      await api.put(`/admin/users/${id}/suspend`);
      setActionMsg('User suspended.');
      loadUsers();
    } catch (e) {
      setActionMsg('Error: ' + e.response?.data?.error);
    }
  }

  async function unsuspend(id) {
    if (!window.confirm('Unsuspend this user and restart their MT5 containers?')) return;
    setActionMsg('Unsuspending user and starting container — can take 1–5 minutes…');
    try {
      const r = await api.put(`/admin/users/${id}/unsuspend`, null, LONG_OP);
      setActionMsg(r.data.message || 'User unsuspended.');
      loadUsers();
    } catch (e) {
      setActionMsg('Error: ' + (e.response?.data?.error || e.message));
    }
  }

  // Container operations are per MT5 ACCOUNT — one user can run several, each
  // with its own container and its own boot outcome.
  async function verifyAccount(accountId) {
    setVerifying(v => ({ ...v, [accountId]: true }));
    setActionMsg('Verifying MT5 account — starting the terminal can take 1–5 minutes '
      + '(boots are serialized). Leave this page open…');
    try {
      const r = await api.put(`/admin/accounts/${accountId}/verify`, null, LONG_OP);
      setActionMsg(r.data.message || 'MT5 account verified.');
      loadUsers();
    } catch (e) {
      const d = e.response?.data || {};
      const detail = d.detail ? ` — ${d.detail}` : '';
      const reason = d.reason ? ` [${d.reason}]` : '';
      setActionMsg(`Error: ${d.error || e.message}${reason}${detail}`);
      loadUsers();
    } finally {
      setVerifying(v => ({ ...v, [accountId]: false }));
    }
  }

  async function restart(accountId) {
    setRestarting(r => ({ ...r, [accountId]: true }));
    setActionMsg('Recreating container — can take 1–5 minutes (boots are serialized)…');
    try {
      await api.post(`/admin/accounts/${accountId}/restart`, null, LONG_OP);
      setActionMsg('Container recreated and starting up. This takes about 60 seconds.');
      loadUsers();
    } catch (e) {
      setActionMsg('Error: ' + (e.response?.data?.error || e.message));
    } finally {
      setRestarting(r => ({ ...r, [accountId]: false }));
    }
  }

  // ── Per-instance lifecycle ─────────────────────────────────────────────────
  // These act on ONE MT5 account. The user-level Suspend/Delete above still
  // exist but hit every container the person owns — with multi-account that is
  // usually too blunt.
  // `opts.forced` is the same action with the override flag set. Disconnect and
  // Delete BOTH refuse over open positions and both need an override, but they
  // are different calls — so the retry is supplied by the caller rather than
  // hardcoded here. It used to always re-issue a disconnect, which would have
  // quietly turned a confirmed Delete into a disconnect.
  async function acctAction(accountId, label, fn, okMsg, opts = {}) {
    setActing(s => ({ ...s, [accountId]: label }));
    setActionMsg(`${label}…`);
    try {
      const r = await fn();
      setActionMsg(r?.data?.message || okMsg);
      loadUsers();
      return r;
    } catch (e) {
      const d = e.response?.data || {};
      // Acting on live mirrored positions comes back as a refusal carrying the
      // count — offer the override rather than dead-ending.
      if (d.code === 'open_positions' && opts.forced) {
        if (window.confirm(
          `${d.error}\n\n${opts.forcePrompt || 'Continue anyway'} and leave `
          + `${d.openPositions} position(s) open at the broker?`
        )) {
          return acctAction(accountId, label, opts.forced, okMsg);
        }
        setActionMsg(`${label.replace(/ing$/, '')} cancelled.`);
        return null;
      }
      setActionMsg('Error: ' + (d.error || e.message));
      return null;
    } finally {
      setActing(s => ({ ...s, [accountId]: null }));
    }
  }

  const stopAccount = (a) => {
    if (!window.confirm(
      `Stop the MT5 container for "${a.displayLabel}"?\n\n`
      + 'Copying on this account pauses. Its open positions stay open at the broker — '
      + 'nothing will manage them until you start it again. The user\'s other accounts '
      + 'are unaffected.'
    )) return null;
    return acctAction(a.id, 'Stopping',
      () => api.post(`/admin/accounts/${a.id}/stop`, null, LONG_OP), 'Container stopped.');
  };

  const startAccount = (a) => acctAction(a.id, 'Starting',
    () => api.post(`/admin/accounts/${a.id}/start`, null, LONG_OP), 'Container starting.');

  const disconnectAccount = (a) => {
    if (!window.confirm(
      `Disconnect "${a.displayLabel}"?\n\n`
      + 'The MT5 container and its volumes are removed. Credentials, trade history and '
      + '(paused) relationships are kept, so it can be reconnected later.'
    )) return null;
    return acctAction(a.id, 'Disconnecting',
      () => api.post(`/admin/accounts/${a.id}/disconnect`, {}, LONG_OP),
      'MT5 account disconnected.', {
        forced: () => api.post(`/admin/accounts/${a.id}/disconnect`, { force: true }, LONG_OP),
        forcePrompt: 'Disconnect anyway',
      });
  };

  // Admin twin of the user's Reconnect. A previously verified account boots
  // straight away; a never-verified one returns to the verify queue.
  const reconnectAccount = (a) => {
    if (!window.confirm(
      `Reconnect "${a.displayLabel}"?\n\n`
      + 'Its container was removed with its volumes at disconnect, so this is a full MT5 '
      + 'reinstall and can take several minutes. Its relationships stay paused until the '
      + 'copier resumes them.'
    )) return null;
    return acctAction(a.id, 'Reconnecting',
      () => api.post(`/admin/accounts/${a.id}/reconnect`, null, LONG_OP),
      'MT5 account reconnecting.');
  };

  // Permanent removal, offered in ANY state. The server still disconnects
  // before deleting (container provably gone before its records go) — this
  // just spares the admin the two-step ritual. Typed confirmation because it
  // destroys trade history, not only a container.
  const deleteAccount = (a) => {
    const live = a.status !== 'disconnected';
    const typed = window.prompt(
      `Permanently DELETE the MT5 account "${a.displayLabel}" (#${a.accountNumber})?\n\n`
      + (live
        ? 'Its container, Wine volumes and Redis credential are destroyed, then its '
        : 'Its ')
      + 'trades, copy executions, relationships and performance history are deleted. '
      + 'The user and their other accounts are untouched. This cannot be undone.\n\n'
      + `Type the account number (${a.accountNumber}) to confirm.`
    );
    if (typed !== a.accountNumber) {
      if (typed != null) setActionMsg('Delete cancelled — account number did not match.');
      return null;
    }
    const url = `/admin/accounts/${a.id}${live ? '?force=true' : ''}`;
    return acctAction(a.id, 'Deleting',
      () => api.delete(url, { timeout: 3 * 60_000 }),
      'MT5 account deleted.', {
        forced: () => api.delete(`${url}${live ? '&' : '?'}stranded=true`,
          { timeout: 3 * 60_000 }),
        forcePrompt: 'Delete anyway',
      });
  };

  async function markLoginComplete(accountId) {
    setTesting(t => ({ ...t, [accountId]: true }));
    setActionMsg('Running live MT5 API test inside the container…');
    try {
      const r = await api.post(`/admin/accounts/${accountId}/mt5-test`, null, { timeout: 3 * 60_000 });
      const t = r.data.test || {};
      if (t.ok) {
        setActionMsg(
          `MT5 API OK — account ${t.account} on ${t.server} | balance ${t.balance} ${t.currency} | equity ${t.equity}. User flipped to running.`
        );
      } else {
        const why = t.error || (t.connected === false ? 'not connected to broker' :
          t.trade_allowed === false ? 'Algo Trading is not enabled' : 'no account info');
        setActionMsg(`MT5 API test failed: ${why}`);
      }
      loadUsers();
    } catch (e) {
      setActionMsg('Error: ' + (e.response?.data?.error || e.message));
    } finally {
      setTesting(t => ({ ...t, [accountId]: false }));
    }
  }

  async function deleteUser(u) {
    const confirmText =
      `Permanently DELETE ${u.email}?\n\n` +
      `This stops and removes their MT5 container, deletes all volumes, ` +
      `copy relationships, trades, executions, snapshots, and notifications. ` +
      `This cannot be undone.\n\nType the email to confirm.`;
    const typed = window.prompt(confirmText);
    if (typed !== u.email) {
      if (typed != null) setActionMsg('Delete cancelled — email did not match.');
      return;
    }
    setDeleting(d => ({ ...d, [u._id]: true }));
    setActionMsg(`Deleting ${u.email}…`);
    try {
      await api.delete(`/admin/users/${u._id}`, { timeout: 2 * 60_000 });
      setActionMsg(`Deleted ${u.email}.`);
      loadUsers();
    } catch (e) {
      setActionMsg('Error: ' + (e.response?.data?.error || e.message));
    } finally {
      setDeleting(d => ({ ...d, [u._id]: false }));
    }
  }

  async function loginAsUser(u) {
    if (!window.confirm(
      `Log in as ${u.email}?\n\n` +
      `You will be switched to their dashboard for support purposes. ` +
      `A "Return to admin" banner will let you switch back.`
    )) return;
    try {
      const r = await api.post(`/admin/users/${u._id}/impersonate`);
      const adminToken        = localStorage.getItem('token');
      const adminRefreshToken = localStorage.getItem('refreshToken');
      if (adminToken)        localStorage.setItem('adminToken', adminToken);
      if (adminRefreshToken) localStorage.setItem('adminRefreshToken', adminRefreshToken);
      localStorage.setItem('impersonatingEmail', u.email);
      localStorage.setItem('token', r.data.token);
      localStorage.removeItem('refreshToken');
      window.location.assign(`/${r.data.user.role}`);
    } catch (e) {
      setActionMsg('Error: ' + (e.response?.data?.error || e.message));
    }
  }

  return (
    <>
      <div className="ct-page-header">
        <div>
          <h1>Users</h1>
          <div className="subtitle">Verify accounts, manage MT5 containers, copy credentials</div>
        </div>
        <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Search username, email, MT5 #"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ minWidth: 220 }}
          />
          <select className="form-control form-control-sm" value={filter.role}
            onChange={e => setFilter(f => ({ ...f, role: e.target.value }))}>
            <option value="">All roles</option>
            <option value="admin">Admin</option>
            <option value="trader">Trader</option>
            <option value="copier">Copier</option>
          </select>
          <select className="form-control form-control-sm" value={filter.status}
            onChange={e => setFilter(f => ({ ...f, status: e.target.value }))}>
            <option value="">All status</option>
            <option value="pending">Pending</option>
            <option value="verified">Verified</option>
            <option value="suspended">Suspended</option>
          </select>
          {(() => {
            // Container state lives on each MT5 ACCOUNT — count accounts, not users.
            const attentionCount = users.reduce((n, u) =>
              n + (u.mt5Accounts || []).filter(a => a.containerStatus === 'needs_attention').length, 0);
            return (
              <button
                className={`btn btn-sm ${attentionOnly ? 'btn-warning' : 'btn-secondary'}`}
                onClick={() => setAttentionOnly(v => !v)}
                title="Show only users with an MT5 account that needs admin attention"
              >
                Needs attention
                {attentionCount > 0 && (
                  <span className="badge badge-light" style={{marginLeft:6}}>{attentionCount}</span>
                )}
              </button>
            );
          })()}
          {(search || filter.role || filter.status || attentionOnly) && (
            <button
              className="btn btn-sm btn-secondary"
              onClick={() => { setSearch(''); setFilter({ role: '', status: '' }); setAttentionOnly(false); }}
            >Clear</button>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h5 className="card-title">User Management</h5>
          <div className="card-category" style={{marginTop:4}}>{total} total</div>
        </div>
        <div className="card-body" style={{padding:0}}>
          {actionMsg && (
            <div className="alert alert-info" style={{margin:16}}>
              {actionMsg}
              <button
                style={{float:'right', background:'transparent', border:'none', color:'inherit', cursor:'pointer'}}
                onClick={() => setActionMsg('')}>×</button>
            </div>
          )}
          {loading ? (
            <div style={{padding:20, color:'var(--text-secondary)'}}>Loading…</div>
          ) : (
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>MT5 accounts</th>
                    <th>Verified</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.filter(u => {
                    const accounts = u.mt5Accounts || [];
                    if (attentionOnly && !accounts.some(a => a.containerStatus === 'needs_attention')) return false;
                    if (!search) return true;
                    const q = search.toLowerCase().trim();
                    return (
                      (u.username && u.username.toLowerCase().includes(q)) ||
                      (u.email    && u.email.toLowerCase().includes(q)) ||
                      accounts.some(a => String(a.accountNumber || '').includes(q))
                    );
                  }).map(u => {
                    const accounts = u.mt5Accounts || [];
                    const role =
                      u.role === 'admin'   ? 'badge-warning' :
                      u.role === 'trader'  ? 'badge-info'    : 'badge-secondary';
                    const status =
                      u.status === 'verified'  ? 'badge-success' :
                      u.status === 'suspended' ? 'badge-danger'  : 'badge-warning';
                    const pendingAccounts = accounts.filter(a => a.status === 'pending').length;

                    return (
                      <React.Fragment key={u._id}>
                        <tr>
                          <td>
                            <div style={{fontWeight:600, color:'var(--text-primary)'}}>
                              {u.username || <span style={{color:'var(--text-tertiary)', fontWeight:400, fontStyle:'italic'}}>no username</span>}
                            </div>
                            <div className="small" style={{color:'var(--text-secondary)', fontSize:11.5}}>{u.email}</div>
                          </td>
                          <td><span className={`badge ${role}`}>{u.role}</span></td>
                          <td><span className={`badge ${status}`}>{u.status}</span></td>
                          <td className="small" style={{color:'var(--text-secondary)'}}>
                            {accounts.length === 0
                              ? <span style={{color:'var(--text-tertiary)'}}>none connected yet</span>
                              : `${accounts.length} connected${pendingAccounts ? ` · ${pendingAccounts} awaiting verify` : ''}`}
                          </td>
                          <td className="small" style={{color:'var(--text-tertiary)'}}>
                            {u.verifiedAt ? new Date(u.verifiedAt).toLocaleDateString() : '—'}
                          </td>
                          <td style={{whiteSpace:'nowrap'}}>
                            <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                              {accounts.length > 0
                                && (u.status === 'pending' || pendingAccounts > 0) && (
                                <button className="btn btn-sm btn-success"
                                  style={{display:'inline-flex',alignItems:'center',gap:4}}
                                  onClick={() => verify(u._id)}
                                  disabled={!!verifying[u._id]}
                                  title="Verify this user and start every MT5 account of theirs that is awaiting verification">
                                  {verifying[u._id]
                                    ? <RotateCw size={13} style={{animation:'spin 1s linear infinite'}} />
                                    : <CheckCircle2 size={13} />}
                                  {verifying[u._id]
                                    ? 'Verifying…'
                                    : (u.status === 'pending' ? 'Verify' : `Verify ${pendingAccounts} account(s)`)}
                                </button>
                              )}
                              {u.status === 'verified' && (
                                <button className="btn btn-sm btn-warning"
                                  style={{display:'inline-flex',alignItems:'center',gap:4}}
                                  onClick={() => suspend(u._id)}>
                                  <Pause size={13} /> Suspend
                                </button>
                              )}
                              {u.status === 'suspended' && (
                                <button className="btn btn-sm btn-success"
                                  style={{display:'inline-flex',alignItems:'center',gap:4}}
                                  onClick={() => unsuspend(u._id)}
                                  title="Restore this user and restart their MT5 containers">
                                  <Play size={13} /> Unsuspend & Start
                                </button>
                              )}
                              {u.role !== 'admin' && u.status !== 'suspended' && (
                                <button className="btn btn-sm btn-info"
                                  onClick={() => loginAsUser(u)}
                                  title="Log in as this user (for support). You can return to admin afterwards."
                                  style={{display:'inline-flex',alignItems:'center',gap:4}}>
                                  <LogIn size={13} /> Login as User
                                </button>
                              )}
                              <button className="btn btn-sm btn-danger"
                                onClick={() => deleteUser(u)}
                                disabled={!!deleting[u._id]}
                                title="Permanently delete this user, all their containers, volumes, and data"
                                style={{display:'inline-flex',alignItems:'center',gap:4}}>
                                <Trash2 size={13} />
                                {deleting[u._id] ? 'Deleting…' : 'Delete'}
                              </button>
                            </div>
                          </td>
                        </tr>

                        {/* One sub-row per MT5 account: container state and the
                            container-level controls all belong to an account,
                            not to the person. */}
                        {accounts.map(a => {
                          const showVnc = a.hasContainer && (
                            a.containerStatus === 'running' ||
                            a.containerStatus === 'starting' ||
                            a.containerStatus === 'awaiting_manual_login' ||
                            a.containerStatus === 'needs_attention'
                          );
                          return (
                            <tr key={a.id}>
                              <td colSpan={6} style={{borderTop:'none', paddingTop:0, paddingBottom:14}}>
                                <div style={{
                                  display:'flex', flexWrap:'wrap', gap:10, alignItems:'center',
                                  fontSize:11.5, paddingLeft:14,
                                  borderLeft:'2px solid var(--border-subtle)',
                                }}>
                                  <span style={{fontWeight:600, color:'var(--text-primary)'}}>
                                    {a.displayLabel}
                                  </span>
                                  <span className={`badge ${
                                    a.status === 'verified' ? 'badge-success'
                                    : a.status === 'disconnected' ? 'badge-secondary'
                                    : a.status === 'suspended' ? 'badge-danger' : 'badge-warning'
                                  }`}>{a.status}</span>
                                  <StatusPill status={a.containerStatus} />
                                  {a.investorPassword && (
                                    <span className="badge badge-info" style={{fontSize:10}}
                                      title="Investor (read-only) password — signal source only, no order execution">
                                      investor
                                    </span>
                                  )}
                                  <CredField label="Login"  value={a.accountNumber} />
                                  <CredField label="Server" value={a.server} />
                                  <RevealCreds accountId={a.id} />

                                  {a.status === 'pending' && (
                                    <button className="btn btn-sm btn-success"
                                      style={{display:'inline-flex',alignItems:'center',gap:4}}
                                      onClick={() => verifyAccount(a.id)}
                                      disabled={!!verifying[a.id]}>
                                      {verifying[a.id]
                                        ? <RotateCw size={13} style={{animation:'spin 1s linear infinite'}} />
                                        : <CheckCircle2 size={13} />}
                                      {verifying[a.id] ? 'Verifying…' : 'Verify & start'}
                                    </button>
                                  )}
                                  {a.status === 'verified' && a.accountNumber && (
                                    <button className="btn btn-sm btn-secondary"
                                      onClick={() => restart(a.id)}
                                      disabled={!!restarting[a.id] || !!acting[a.id]}
                                      style={{display:'inline-flex',alignItems:'center',gap:4}}>
                                      <RotateCw size={13} style={{animation: restarting[a.id] ? 'spin 1s linear infinite':'none'}} />
                                      {restarting[a.id] ? 'Restarting…' : 'Restart'}
                                    </button>
                                  )}
                                  {/* Stop / Start this ONE instance. Stopping parks the
                                      account as 'suspended', which is what keeps the health
                                      watcher from raising container_down on a container an
                                      admin took down deliberately. */}
                                  {a.status === 'verified' && a.hasContainer && (
                                    <button className="btn btn-sm btn-warning"
                                      onClick={() => stopAccount(a)}
                                      disabled={!!acting[a.id]}
                                      title="Stop this MT5 container and pause its copying (other accounts unaffected)"
                                      style={{display:'inline-flex',alignItems:'center',gap:4}}>
                                      <Square size={12} />
                                      {acting[a.id] === 'Stopping' ? 'Stopping…' : 'Stop'}
                                    </button>
                                  )}
                                  {a.status === 'suspended' && a.accountNumber && (
                                    <button className="btn btn-sm btn-success"
                                      onClick={() => startAccount(a)}
                                      disabled={!!acting[a.id]}
                                      title="Start this MT5 container again"
                                      style={{display:'inline-flex',alignItems:'center',gap:4}}>
                                      <Play size={13} />
                                      {acting[a.id] === 'Starting' ? 'Starting…' : 'Start'}
                                    </button>
                                  )}
                                  {showVnc && (
                                    <a className="btn btn-sm btn-primary"
                                      href={`/novnc/${a.id}/vnc.html?path=novnc/${a.id}/websockify&autoconnect=1&resize=scale`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      title="Open the noVNC web viewer to log into MT5 manually"
                                      style={{display:'inline-flex',alignItems:'center',gap:4,textDecoration:'none'}}>
                                      <ExternalLink size={13} /> VNC
                                    </a>
                                  )}
                                  {a.hasContainer && (
                                    <button className="btn btn-sm btn-secondary"
                                      onClick={() => setLogsFor({ id: a.id, email: `${u.email} — ${a.displayLabel}` })}
                                      title="Stream live container logs"
                                      style={{display:'inline-flex',alignItems:'center',gap:4}}>
                                      <FileText size={13} /> Logs
                                    </button>
                                  )}
                                  {showVnc && (
                                    <button className="btn btn-sm btn-success"
                                      onClick={() => markLoginComplete(a.id)}
                                      disabled={!!testing[a.id]}
                                      title="Confirm MT5 login is complete and run a live API test (terminal_info + account_info)"
                                      style={{display:'inline-flex',alignItems:'center',gap:4}}>
                                      <PlugZap size={13} />
                                      {testing[a.id] ? 'Testing…' : 'Mark Login Complete'}
                                    </button>
                                  )}
                                  {/* Disconnect is the REVERSIBLE one: the container and its
                                      volumes go, credentials and history stay, so it can be
                                      reconnected. Delete is offered in every state — the
                                      server disconnects first when it has to, so the
                                      container is still provably gone before the records
                                      referencing it are destroyed. */}
                                  {a.status !== 'disconnected' && (
                                    <button className="btn btn-sm btn-warning"
                                      onClick={() => disconnectAccount(a)}
                                      disabled={!!acting[a.id]}
                                      title="Destroys the container, its Wine volumes and its Redis credential. Keeps login details, trade history and (paused) relationships, so it can be reconnected — but reconnecting rebuilds MT5 from scratch (~25 min)."
                                      style={{display:'inline-flex',alignItems:'center',gap:4}}>
                                      <Unplug size={13} />
                                      {acting[a.id] === 'Disconnecting' ? 'Disconnecting…' : 'Disconnect'}
                                    </button>
                                  )}
                                  {a.status === 'disconnected' && (
                                    <button className="btn btn-sm btn-success"
                                      onClick={() => reconnectAccount(a)}
                                      disabled={!!acting[a.id]}
                                      title="Bring this account back. A previously verified account boots immediately (full MT5 reinstall); a never-verified one returns to the verify queue."
                                      style={{display:'inline-flex',alignItems:'center',gap:4}}>
                                      <Plug size={13} />
                                      {acting[a.id] === 'Reconnecting' ? 'Reconnecting…' : 'Reconnect'}
                                    </button>
                                  )}
                                  <button className="btn btn-sm btn-danger"
                                    onClick={() => deleteAccount(a)}
                                    disabled={!!acting[a.id]}
                                    title={a.status === 'disconnected'
                                      ? 'Permanently delete this MT5 account and its history (the user is untouched)'
                                      : 'Permanently delete this MT5 account: destroys its container and volumes, then deletes its trades, executions, relationships and snapshots. The user and their other accounts are untouched.'}
                                    style={{display:'inline-flex',alignItems:'center',gap:4}}>
                                    <Trash2 size={13} />
                                    {acting[a.id] === 'Deleting' ? 'Deleting…' : 'Delete permanently'}
                                  </button>
                                  {a.containerError && (
                                    <span style={{
                                      color:'var(--loss)', maxWidth:320,
                                      whiteSpace:'normal', wordBreak:'break-word',
                                    }} title={a.containerError}>
                                      {a.containerError}
                                    </span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
              <div style={{
                display:'flex',justifyContent:'space-between',alignItems:'center',
                padding:'14px 20px',borderTop:'1px solid var(--border-subtle)'
              }}>
                <span style={{color:'var(--text-tertiary)',fontSize:12}}>
                  Page {page} — {users.length} / {total}
                </span>
                <div style={{display:'flex',gap:6}}>
                  <button className="btn btn-sm btn-secondary" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</button>
                  <button className="btn btn-sm btn-secondary" disabled={page * 20 >= total} onClick={() => setPage(p => p + 1)}>Next</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`@keyframes spin { from{transform:rotate(0)} to{transform:rotate(360deg)} }`}</style>

      {logsFor && (
        <LogsModal
          accountId={logsFor.id}
          email={logsFor.email}
          onClose={() => setLogsFor(null)}
        />
      )}
    </>
  );
}

function LogsModal({ accountId, email, onClose }) {
  const [logs, setLogs] = useState('');
  const [error, setError] = useState('');
  const [paused, setPaused] = useState(false);
  const [autoscroll, setAutoscroll] = useState(true);
  const [loading, setLoading] = useState(true);
  const preRef = useRef(null);
  const pausedRef = useRef(paused);
  useEffect(() => { pausedRef.current = paused; }, [paused]);

  useEffect(() => {
    let cancelled = false;

    async function tick() {
      if (pausedRef.current) return;
      try {
        const r = await api.get(`/admin/accounts/${accountId}/logs`, { params: { tail: 800 } });
        if (cancelled) return;
        setLogs(r.data.logs || '');
        setError('');
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        setError(e.response?.data?.error || e.message);
        setLoading(false);
      }
    }

    tick();
    const id = setInterval(tick, 2000);
    return () => { cancelled = true; clearInterval(id); };
  }, [accountId]);

  // Auto-scroll to bottom on update
  useEffect(() => {
    if (!autoscroll || !preRef.current) return;
    preRef.current.scrollTop = preRef.current.scrollHeight;
  }, [logs, autoscroll]);

  // Close on Esc
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{
        position:'fixed', inset:0, zIndex:200,
        background:'rgba(0,0,0,0.65)',
        display:'flex', alignItems:'center', justifyContent:'center', padding:16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width:'min(1100px, 100%)', maxHeight:'90vh',
          display:'flex', flexDirection:'column',
          background:'var(--bg-surface)',
          border:'1px solid var(--border-subtle)',
          borderRadius:'var(--radius-md)',
          boxShadow:'0 20px 60px rgba(0,0,0,0.55)',
          overflow:'hidden',
        }}
      >
        <div style={{
          display:'flex', alignItems:'center', gap:12,
          padding:'12px 16px',
          borderBottom:'1px solid var(--border-subtle)',
          background:'var(--bg-surface-2)',
        }}>
          <FileText size={16} />
          <div style={{flex:1, minWidth:0}}>
            <div style={{fontSize:13, fontWeight:600, color:'var(--text-primary)'}}>
              Container logs
            </div>
            <div style={{fontSize:11, color:'var(--text-tertiary)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>
              {email}{paused ? ' • paused' : ' • live (2s poll)'}
            </div>
          </div>
          <label style={{display:'inline-flex', alignItems:'center', gap:6, fontSize:12, color:'var(--text-secondary)', cursor:'pointer'}}>
            <input type="checkbox" checked={autoscroll} onChange={e => setAutoscroll(e.target.checked)} />
            Auto-scroll
          </label>
          <button
            className="btn btn-sm btn-secondary"
            onClick={() => setPaused(p => !p)}
            style={{display:'inline-flex', alignItems:'center', gap:4}}
            title={paused ? 'Resume polling' : 'Pause polling'}
          >
            {paused ? <><Play size={13}/> Resume</> : <><PauseIcon size={13}/> Pause</>}
          </button>
          <button
            className="btn btn-sm btn-secondary"
            onClick={onClose}
            aria-label="Close"
            style={{display:'inline-flex', alignItems:'center', gap:4}}
          >
            <X size={14}/>
          </button>
        </div>

        {error && (
          <div className="alert alert-danger" style={{margin:12}}>
            {error}
          </div>
        )}

        <pre
          ref={preRef}
          style={{
            flex:1, margin:0, padding:'12px 16px',
            overflow:'auto',
            background:'#0a0e1a',
            color:'#d4d8e6',
            fontFamily:'"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize:12, lineHeight:1.45,
            whiteSpace:'pre-wrap', wordBreak:'break-word',
            minHeight:240,
          }}
        >
          {loading ? 'Loading logs…' : (logs || '(no log output yet)')}
        </pre>
      </div>
    </div>
  );
}

// Passwords are fetched on demand (audit-logged server-side) instead of
// riding the bulk user list into the DOM on every 5s poll.
function RevealCreds({ accountId }) {
  const [creds, setCreds] = useState(null);
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState('');

  async function reveal() {
    setBusy(true);
    setError('');
    try {
      const r = await api.get(`/admin/accounts/${accountId}/credentials`);
      setCreds(r.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  }

  if (creds) {
    return (
      <>
        <CredField label="MT5 Password" value={creds.mt5Password} />
        {creds.vncPassword && <CredField label="VNC Password" value={creds.vncPassword} />}
        <button className="btn btn-sm btn-secondary"
          onClick={() => setCreds(null)}
          style={{fontSize:10.5, padding:'2px 8px'}}>
          Hide
        </button>
      </>
    );
  }
  return (
    <>
      <button className="btn btn-sm btn-secondary"
        onClick={reveal}
        disabled={busy}
        title="Fetch and display this user's MT5/VNC passwords (the reveal is audit-logged)"
        style={{display:'inline-flex',alignItems:'center',gap:4, fontSize:10.5, padding:'2px 8px'}}>
        <Eye size={12} /> {busy ? 'Revealing…' : 'Reveal passwords'}
      </button>
      {error && <span style={{color:'var(--loss)', fontSize:11}}>{error}</span>}
    </>
  );
}

function CredField({ label, value }) {
  return (
    <span style={{display:'inline-flex',alignItems:'center',gap:6}}>
      <span style={{
        textTransform:'uppercase',letterSpacing:'0.06em',color:'var(--text-tertiary)',
        fontSize:10.5,fontWeight:600
      }}>{label}</span>
      <CopyChip value={value} label={label.toLowerCase()} />
    </span>
  );
}
